import { TextField } from '@cmg/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import type { VFC } from 'react';
import { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import usePrevious from '../../../../../../../common/util/usePrevious';
import { ServerErrorAlert } from '../../../../../../../design-system/alert/ServerErrorAlert';
import { ConfirmationDialog } from '../../../../../../../design-system/dialog/confirmation/ConfirmationDialog';
import { FormDialogContent } from '../../../../../../../design-system/dialog/FormDialogContent';
import { FormDialogContentItem } from '../../../../../../../design-system/dialog/FormDialogContentItem';
import type { OpenIdConnectConfigurationUpdate } from '../../../../../../../types/domain/identity-provider/configurations/openIdConnect';
import {
  IdentityProviderType,
  identityProviderTypeDisplay,
} from '../../../../../../../types/domain/identity-provider/constants';
import {
  resetState,
  selectOpenIdConnectConfigError,
  selectOpenIdConnectConfigSubmitting,
  updateOpenIdConnectConfig,
} from '../../../../../../account-detail/security/identity-providers/ducks';
import { OpenIdConfigurationUpdateSchema } from '../utils';

const mapStateToProps = state => ({
  error: selectOpenIdConnectConfigError(state),
  isSubmitting: selectOpenIdConnectConfigSubmitting(state),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      updateOpenIdConnectConfig,
      resetState,
    },
    dispatch
  ),
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;

export type Props = StateProps &
  DispatchProps & {
    isOpened: Readonly<boolean>;
    onClose: () => void;
  };

export const ConfigureOpenIdProviderDialogComponent: VFC<Props> = ({
  isOpened,
  isSubmitting,
  actions: { resetState, updateOpenIdConnectConfig },
  error,
  onClose,
}) => {
  const navigate = useNavigate();

  const formik = useFormik<OpenIdConnectConfigurationUpdate>({
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: OpenIdConfigurationUpdateSchema,
    initialValues: {
      name: '',
      configuration: {
        clientId: '',
        authority: '',
      },
      enabled: false,
    },
    onSubmit: configuration => {
      updateOpenIdConnectConfig({
        navigate,
        redirectToDetailsPage: true,
        saveAndTest: false,
        configuration,
      });
    },
  });

  const { resetForm } = formik;
  const previousIsOpened = usePrevious(isOpened);
  useEffect(() => {
    if (!isOpened && previousIsOpened) {
      resetForm();
      resetState();
    }
  }, [isOpened, previousIsOpened, resetForm, resetState]);

  return (
    <FormikProvider value={formik}>
      <ConfirmationDialog
        maxWidth="xs"
        title={`Configure ${identityProviderTypeDisplay[IdentityProviderType.OPEN_ID_CONNECT]}`}
        isOpen={isOpened}
        isLoading={isSubmitting}
        cancelButtonLabel="Cancel"
        onCancel={onClose}
        submitButtonLabel="Add Provider"
        submitButtonColor="primary"
        onSubmit={() => formik.handleSubmit()}
        content={
          <Form noValidate={true}>
            <FormDialogContent
              error={error && <ServerErrorAlert title="Error" error={error} />}
              items={
                <Fragment>
                  <FormDialogContentItem
                    value={
                      <TextField
                        required
                        label="Name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    }
                  />
                  <FormDialogContentItem
                    value={
                      <TextField
                        required
                        label="Client ID"
                        name="configuration.clientId"
                        value={formik.values.configuration.clientId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.configuration?.clientId &&
                          Boolean(formik.errors.configuration?.clientId)
                        }
                        helperText={
                          formik.touched.configuration?.clientId &&
                          formik.errors.configuration?.clientId
                        }
                      />
                    }
                  />
                  <FormDialogContentItem
                    value={
                      <TextField
                        required
                        label="Authority"
                        name="configuration.authority"
                        value={formik.values.configuration.authority}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.configuration?.authority &&
                          Boolean(formik.errors.configuration?.authority)
                        }
                        helperText={
                          formik.touched.configuration?.authority &&
                          formik.errors.configuration?.authority
                        }
                      />
                    }
                  />
                </Fragment>
              }
            />
          </Form>
        }
      />
    </FormikProvider>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigureOpenIdProviderDialogComponent);
